import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from "../../Components/ui";
import { siteBillingService } from "../../api/services/site-billing.service";
import {
  SiteBillingInvoice,
  SiteBillingInvoiceDetail,
  SiteBillingPreviewSite,
} from "../../api/types/site-billing";
import { currentBillingMonth, formatMonth, formatWon, statusLabel } from "./siteBillingFormat";

type InvoiceFilters = {
  billing_month: string;
  status: string;
  source_status: string;
  overdue: string;
  page: number;
};

const initialFilters = (): InvoiceFilters => ({
  billing_month: currentBillingMonth(),
  status: "",
  source_status: "",
  overdue: "",
  page: 1,
});

const getPreviewStatusCount = (sites: SiteBillingPreviewSite[] = [], status: string) =>
  sites.filter((site) => site.status === status || site.source_status === status).length;

const previewStatus = (site: SiteBillingPreviewSite) => site.source_status || site.status || site.billing_state || "-";

const SiteBillingInvoicesPage = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<InvoiceFilters>(initialFilters);
  const [previewSites, setPreviewSites] = useState<SiteBillingPreviewSite[]>([]);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
  const [anomalyAcceptSiteIds, setAnomalyAcceptSiteIds] = useState<number[]>([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paid_at: "",
    depositor_name: "",
    memo: "",
  });
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: "",
    reason: "",
  });

  const invoiceQuery = useQuery({
    queryKey: ["site-billing", "invoices", filters],
    queryFn: () => siteBillingService.getInvoices(filters),
  });

  const invoiceDetailQuery = useQuery({
    queryKey: ["site-billing", "invoice", selectedInvoiceId],
    queryFn: () => siteBillingService.getInvoiceDetail(Number(selectedInvoiceId)),
    enabled: Boolean(selectedInvoiceId),
  });

  const invoices = invoiceQuery.data?.data.results ?? [];
  const detail = invoiceDetailQuery.data?.data as SiteBillingInvoiceDetail | undefined;

  const previewMutation = useMutation({
    mutationFn: () =>
      siteBillingService.generateInvoicesPreview({
        billing_month: filters.billing_month,
      }),
    onSuccess: (response) => {
      const nextSites = response.data.sites ?? [];
      setPreviewId(response.data.preview_id ?? null);
      setPreviewSites(nextSites);
      setSelectedSiteIds([]);
      setAnomalyAcceptSiteIds([]);
      toast.success("청구서 미리보기를 생성했습니다.");
    },
    onError: () => toast.error("청구서 미리보기 생성에 실패했습니다."),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      siteBillingService.applyInvoicesPreview({
        preview_id: Number(previewId),
        selected_site_ids: selectedSiteIds,
        anomaly_accept_site_ids: anomalyAcceptSiteIds,
      }),
    onSuccess: () => {
      toast.success("선택한 청구서를 생성했습니다.");
      queryClient.invalidateQueries({ queryKey: ["site-billing", "invoices"] });
    },
    onError: () => toast.error("청구서 생성에 실패했습니다."),
  });

  const paymentMutation = useMutation({
    mutationFn: () =>
      siteBillingService.createPayment(Number(selectedInvoiceId), {
        amount: Number(paymentForm.amount),
        paid_at: paymentForm.paid_at,
        depositor_name: paymentForm.depositor_name,
        entry_type: "bank_transfer",
        memo: paymentForm.memo,
      }),
    onSuccess: () => {
      toast.success("입금 승인을 기록했습니다.");
      setPaymentForm({ amount: "", paid_at: "", depositor_name: "", memo: "" });
      queryClient.invalidateQueries({ queryKey: ["site-billing"] });
    },
    onError: () => toast.error("입금 승인 기록에 실패했습니다."),
  });

  const adjustmentMutation = useMutation({
    mutationFn: () =>
      siteBillingService.createAdjustment(Number(selectedInvoiceId), {
        amount: Number(adjustmentForm.amount),
        reason: adjustmentForm.reason,
      }),
    onSuccess: () => {
      toast.success("수동 조정을 추가했습니다.");
      setAdjustmentForm({ amount: "", reason: "" });
      queryClient.invalidateQueries({ queryKey: ["site-billing"] });
    },
    onError: () => toast.error("수동 조정 추가에 실패했습니다."),
  });

  const previewCounts = useMemo(
    () => ({
      ready: getPreviewStatusCount(previewSites, "ready"),
      skipped: getPreviewStatusCount(previewSites, "skipped"),
      missing: getPreviewStatusCount(previewSites, "missing"),
      partial: getPreviewStatusCount(previewSites, "partial"),
      anomaly: getPreviewStatusCount(previewSites, "anomaly"),
    }),
    [previewSites]
  );

  const updateFilter = (key: keyof InvoiceFilters, value: string | number) => {
    setFilters((previous) => ({ ...previous, [key]: value, page: key === "page" ? Number(value) : 1 }));
  };

  const toggleSiteSelection = (siteId: number) => {
    setSelectedSiteIds((previous) =>
      previous.includes(siteId) ? previous.filter((id) => id !== siteId) : [...previous, siteId]
    );
  };

  const toggleAnomalyAccept = (siteId: number) => {
    setAnomalyAcceptSiteIds((previous) =>
      previous.includes(siteId) ? previous.filter((id) => id !== siteId) : [...previous, siteId]
    );
  };

  const exportCsv = () => {
    window.open(siteBillingService.buildInvoiceExportUrl(filters), "_blank", "noopener,noreferrer");
  };

  return (
    <Container fluid>
      <BreadCrumb title="청구서 관리" pageTitle="사용료 관리" />

      <Card className="mb-3">
        <CardHeader>청구서 필터</CardHeader>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={2}>
              <Label htmlFor="billing-month">청구월</Label>
              <Input
                id="billing-month"
                type="month"
                value={filters.billing_month}
                onChange={(event) => updateFilter("billing_month", event.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label htmlFor="invoice-status">납부상태</Label>
              <Input
                id="invoice-status"
                type="select"
                value={filters.status}
                onChange={(event) => updateFilter("status", event.target.value)}
              >
                <option value="">전체</option>
                <option value="unpaid">미납</option>
                <option value="partial_paid">부분납</option>
                <option value="paid">완납</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label htmlFor="source-status">정산소스</Label>
              <Input
                id="source-status"
                type="select"
                value={filters.source_status}
                onChange={(event) => updateFilter("source_status", event.target.value)}
              >
                <option value="">전체</option>
                <option value="complete">정상</option>
                <option value="partial">부분</option>
                <option value="missing">결측</option>
                <option value="anomaly">확인필요</option>
              </Input>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap gap-2">
                <Button onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
                  미리보기
                </Button>
                <Button color="soft-secondary" onClick={exportCsv}>
                  CSV 내보내기
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {previewId ? (
        <Card className="mb-3">
          <CardHeader>청구서 생성 preview #{previewId}</CardHeader>
          <CardBody>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {Object.entries(previewCounts).map(([key, value]) => (
                <Badge key={key} color="secondary">
                  {key}: {value}
                </Badge>
              ))}
            </div>
            <div className="w-100 overflow-auto">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>적용</th>
                    <th>지점</th>
                    <th>상태</th>
                    <th className="text-end">청구액</th>
                    <th>anomaly</th>
                  </tr>
                </thead>
                <tbody>
                  {previewSites.map((site) => (
                    <tr key={site.site_id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${site.site_name} 적용 선택`}
                          checked={selectedSiteIds.includes(site.site_id)}
                          onChange={() => toggleSiteSelection(site.site_id)}
                        />
                      </td>
                      <td>{site.site_name}</td>
                      <td>{previewStatus(site)}</td>
                      <td className="text-end">{formatWon(site.amount_due)}</td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${site.site_name} anomaly 금액 반영`}
                          checked={anomalyAcceptSiteIds.includes(site.site_id)}
                          disabled={previewStatus(site) !== "anomaly"}
                          onChange={() => toggleAnomalyAccept(site.site_id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              disabled={!previewId || selectedSiteIds.length === 0 || applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
            >
              선택 청구서 생성
            </Button>
          </CardBody>
        </Card>
      ) : null}

      <Row className="g-3">
        <Col lg={selectedInvoiceId ? 8 : 12}>
          <Card>
            <CardHeader>월별 청구서</CardHeader>
            <CardBody>
              {invoiceQuery.isLoading ? <div>로딩 중</div> : null}
              <div className="w-100 overflow-auto">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>청구번호</th>
                      <th>지점</th>
                      <th>청구월</th>
                      <th>상태</th>
                      <th>소스</th>
                      <th className="text-end">청구액</th>
                      <th className="text-end">잔액</th>
                      <th>납부기한</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice: SiteBillingInvoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoice_number}</td>
                        <td>
                          {invoice.site_name}
                          {invoice.site_subdomain ? <div className="text-muted small">{invoice.site_subdomain}</div> : null}
                        </td>
                        <td>{formatMonth(invoice.billing_month)}</td>
                        <td>{statusLabel(invoice.status)}</td>
                        <td>{statusLabel(invoice.source_status)}</td>
                        <td className="text-end">{formatWon(invoice.amount_due)}</td>
                        <td className="text-end">잔액 {formatWon(invoice.balance_due)}</td>
                        <td>{invoice.due_date}</td>
                        <td>
                          <Button size="sm" color="soft-primary" onClick={() => setSelectedInvoiceId(invoice.id)}>
                            상세
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>

        {selectedInvoiceId ? (
          <Col lg={4}>
            <Card>
              <CardHeader>청구서 상세</CardHeader>
              <CardBody>
                {invoiceDetailQuery.isLoading ? <div>상세 로딩 중</div> : null}
                {detail ? (
                  <>
                    <h5 className="mb-3">{detail.invoice_number}</h5>
                    <div className="mb-3">
                      <div>계좌입금 승인</div>
                      <Label htmlFor="payment-amount">입금액</Label>
                      <Input
                        id="payment-amount"
                        type="number"
                        value={paymentForm.amount}
                        onChange={(event) => setPaymentForm((previous) => ({ ...previous, amount: event.target.value }))}
                      />
                      <Label htmlFor="payment-paid-at" className="mt-2">입금일시</Label>
                      <Input
                        id="payment-paid-at"
                        type="datetime-local"
                        value={paymentForm.paid_at}
                        onChange={(event) => setPaymentForm((previous) => ({ ...previous, paid_at: event.target.value }))}
                      />
                      <Label htmlFor="payment-depositor" className="mt-2">입금자명</Label>
                      <Input
                        id="payment-depositor"
                        value={paymentForm.depositor_name}
                        onChange={(event) => setPaymentForm((previous) => ({ ...previous, depositor_name: event.target.value }))}
                      />
                      <Button
                        className="mt-2"
                        disabled={!paymentForm.amount || !paymentForm.paid_at || !paymentForm.depositor_name || paymentMutation.isPending}
                        onClick={() => paymentMutation.mutate()}
                      >
                        입금 승인
                      </Button>
                    </div>

                    <div className="mb-3">
                      <div>수동 조정</div>
                      <Label htmlFor="adjustment-amount">조정액</Label>
                      <Input
                        id="adjustment-amount"
                        type="number"
                        value={adjustmentForm.amount}
                        onChange={(event) => setAdjustmentForm((previous) => ({ ...previous, amount: event.target.value }))}
                      />
                      <Label htmlFor="adjustment-reason" className="mt-2">조정 사유</Label>
                      <Input
                        id="adjustment-reason"
                        value={adjustmentForm.reason}
                        onChange={(event) => setAdjustmentForm((previous) => ({ ...previous, reason: event.target.value }))}
                      />
                      <Button
                        className="mt-2"
                        disabled={!adjustmentForm.amount || !adjustmentForm.reason || adjustmentMutation.isPending}
                        onClick={() => adjustmentMutation.mutate()}
                      >
                        조정 추가
                      </Button>
                    </div>

                    <div className="small text-muted">
                      결제 {detail.payments?.length ?? 0}건 · 조정 {detail.adjustments?.length ?? 0}건
                    </div>
                  </>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        ) : null}
      </Row>
    </Container>
  );
};

export default SiteBillingInvoicesPage;
